import {
  resolveRouteParam,
  useIsFocused,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from "@-/com.npmjs.expo-router";
import { Platform, View } from "@-/com.npmjs.react-native";
import { GlassHeaderOverlapContext } from "@-/dev.improve.haniverse.components.ui.header-scroll";
import {
  isScrollEdgeEffectSupported,
  ScrollEdgeTopExtensionContext,
  ScrollEdgeTopInsetContext,
} from "@-/dev.improve.haniverse.modules.scroll-edge-effect";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useNavToolbarPublisher } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useCallback, useContext, useLayoutEffect, useMemo, type ReactNode } from "react";
import {
  RouteBodyStateScope,
  useRouteBodyRef,
  useRouteBodyState,
} from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { MorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { MorphemePanelsActionsToolbar } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.toolbar";
import { isMorphemeCardActionsMenuInTabBar } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.card-actions";
import { MorphemeFlashcardActionsMenuButton } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.card-actions/menu-button";
import { GraphemeInfoView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.info";
import { MorphemeMnemonicsView } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics";
import { useMorphemeRouteBodyReady } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.route-body";
import { MorphemeFlashcardPanelBackContext } from "#morpheme-card-panel-back-context";

/** Details keeps the card actions beside its bottom tabs instead of over its content. */
function renderDetailsTabBarAccessory(diameter: number) {
  return <MorphemeFlashcardActionsMenuButton diameter={diameter} />;
}

/** A single glyph / pronunciation / Details pager, shared by every grapheme route family. */
export function MorphemeFlashcardPanel() {
  const [grapheme] = useContextSelectors(MorphemePanelsContext, (c) => c.character);
  const onBack = useContext(MorphemeFlashcardPanelBackContext);
  const router = useRouter();
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const params = useLocalSearchParams<{ tab?: string | string[]; view?: string | string[] }>();
  const onReady = useMorphemeRouteBodyReady();
  const view = resolveRouteParam(params.view);
  const tab = resolveRouteParam(params.tab);
  const routeView =
    view === "mnemonics" || (view !== "info" && tab === "mnemonics") ? "mnemonics" : "info";
  const [selectedView, setSelectedView] = useRouteBodyState(
    "morpheme-card-panel-view:" + grapheme,
    routeView,
  );
  const routeViewKey = JSON.stringify([view, tab]);
  const previousRouteView = useRouteBodyRef(
    "morpheme-card-panel-route-view:" + grapheme,
    routeViewKey,
  );
  useLayoutEffect(() => {
    // A body reacquired without params keeps its selection; explicit deep links still win.
    if ((view !== "" || tab !== "") && previousRouteView.current !== routeViewKey) {
      previousRouteView.current = routeViewKey;
      setSelectedView(routeView);
    }
  }, [previousRouteView, routeView, routeViewKey, setSelectedView, tab, view]);
  const isMnemonic = selectedView === "mnemonics";
  const onDetailsChange = useCallback(
    (selected: boolean) => {
      // Detached native pages can acknowledge a selection; they must never alter a focused route.
      if (!navigation.isFocused()) {
        return false;
      }
      const nextView = selected ? "info" : "mnemonics";
      setSelectedView(nextView);
      if (nextView !== selectedView) {
        router.setParams({ view: nextView });
      }
      return true;
    },
    [navigation, router, selectedView, setSelectedView],
  );
  const { height: toolbarHeight } = useToolbarGridMetrics();
  const toolbarPublisher = useNavToolbarPublisher();
  const topExtension = useContext(ScrollEdgeTopExtensionContext);
  const topInset = useContext(ScrollEdgeTopInsetContext);
  const isToolbarFloating = toolbarPublisher === null;
  const isGlassFloating =
    isToolbarFloating && (isScrollEdgeEffectSupported || Platform.OS === "android");
  const pagerTopExtension = isGlassFloating ? topExtension : 0;
  const contentTopInset = isGlassFloating ? toolbarHeight + pagerTopExtension + topInset : 0;
  const isDictionary = tab === "" || tab === "mnemonics" || tab === "dictionary";
  const detailsTopInset = isDictionary ? 0 : contentTopInset;
  const details = useCallback(
    () => (
      <RouteBodyStateScope name="morpheme-card-info">
        <GlassHeaderOverlapContext
          value={isGlassFloating && isDictionary ? contentTopInset - topInset : undefined}
        >
          <View className="min-h-0 flex-1" style={{ paddingTop: detailsTopInset }}>
            <GraphemeInfoView
              grapheme={grapheme}
              tabBarAccessory={
                isMorphemeCardActionsMenuInTabBar ? renderDetailsTabBarAccessory : undefined
              }
            />
          </View>
        </GlassHeaderOverlapContext>
      </RouteBodyStateScope>
    ),
    [contentTopInset, detailsTopInset, grapheme, isDictionary, isGlassFloating, topInset],
  );
  const renderTabBar = useCallback(
    (tabBar: ReactNode, actions?: ReactNode) => (
      <MorphemePanelsActionsToolbar
        activePanel={isMnemonic ? "mnemonics" : "info"}
        floating={isToolbarFloating}
        onBack={onBack}
        readingActions={actions}
        readingTabBar={tabBar}
      />
    ),
    [isMnemonic, isToolbarFloating, onBack],
  );
  const pageTabs = useMemo(
    () => ({
      contentTopInset,
      details,
      isDetailsSelected: !isMnemonic,
      onDetailsChange,
      pagerTopExtension,
      renderTabBar,
      swipeEnabled: isFocused,
    }),
    [
      contentTopInset,
      details,
      isMnemonic,
      onDetailsChange,
      pagerTopExtension,
      renderTabBar,
      isFocused,
    ],
  );

  return (
    <View className="min-h-0 flex-1 bg-background" onLayout={onReady ?? undefined}>
      <ScrollEdgeTopExtensionContext value={topExtension - pagerTopExtension}>
        <RouteBodyStateScope name="morpheme-card-mnemonics">
          <GlassHeaderOverlapContext value={undefined}>
            <MorphemeMnemonicsView
              pageTabs={pageTabs}
              rawGrapheme={grapheme}
              readingNavigation="browse"
            />
          </GlassHeaderOverlapContext>
        </RouteBodyStateScope>
      </ScrollEdgeTopExtensionContext>
    </View>
  );
}
